/**
 * BLRRD render kernel — the canonical per-pixel pipeline (ACES era).
 *
 * Phase 2.C-D switch-over: kernel now consumes Float32 ACEScg-linear input
 * and produces Float32 ACEScg-linear output. The previous Uint8 quantize
 * + sRGB-pseudo-linear lived inside this function; both moved out.
 *
 *   Ingest  (src/pipeline/ingest.js)        sRGB uint8     → ACEScg Float32
 *   Kernel  (this file)                     ACEScg Float32 → ACEScg Float32
 *   ViewTx  (src/pipeline/view-transform.js) ACEScg Float32 → display Uint8
 *
 * Codex review fixes integrated:
 *   #1 No view transform here — kernel does NOT clamp to [0,1] or encode.
 *      filmicACES + ODT live in view-transform.js so highlights >1 survive.
 *   #4 IDT NOT applied per-frame — already in ingest cache.
 *   #5 Curves panel evaluates in ACEScct (perceptual log) for Resolve-feel.
 *   #6 HSL stage detours through linear-sRGB so hue rotations remain
 *      geometrically sensible (HSL is undefined in AP1 wide-gamut).
 *   Slider recalibrations vs Codex' "math unchanged in shape" critique:
 *      - Contrast pivot:       0.5    → 0.18 (mid-grey ACEScg-linear)
 *      - Dehaze pivot:         0.5    → 0.18
 *      - Tone-range thresholds: now in ACEScct space (perceptual log)
 *
 * Pure function. No DOM access, no closure over `E`. Identical-by-design
 * across:
 *   - main thread sync render
 *   - render Worker
 *   - .cube LUT bake (export-LDR variant)
 *
 * Pixel-hash gate refreshes baselines on the ACES switch-over PR.
 */

import { rgbToHslF, hslToRgbF } from '../color/index.js';
import { HSL_RANGES } from '../color/hsl-ranges.js';
import { encodeACEScct, decodeACEScct } from '../color/aces.js';
import { M_ACEScg_to_sRGB, M_sRGB_to_ACEScg } from '../color/spaces.js';

/* Local clamps. */
const clamp   = (v, lo, hi) => v < lo ? lo : v > hi ? hi : v;
const clamp01 = (v) => v < 0 ? 0 : v > 1 ? 1 : v;

/* Smoothstep — Hermite interpolation, C¹-continuous at both endpoints.
 * Replaces the C¹-discontinuous piecewise-linear ramps that produced
 * visible kinks at tone-band edges (Phase 1.H morning-bugs fix). */
function smoothstep(x, edge0, edge1) {
    if (edge0 === edge1) return x < edge0 ? 0 : 1;
    const t = clamp01((x - edge0) / (edge1 - edge0));
    return t * t * (3 - 2 * t);
}

/* Tone-range edges in ACEScct (perceptual log) space. ACEScct landmarks:
 *   sRGB 0   → 0.073   sRGB 32  → 0.196    sRGB 64  → 0.282
 *   sRGB 128 → 0.4135  sRGB 192 → 0.485    sRGB 240 → 0.539
 *   sRGB 255 → 0.555   linear 1.0 → 0.555  linear 3.18 → 0.65
 *
 * Phase 1.H morning-bugs fix (2026-05-07): retuned for SDR reachability +
 * smoothstep blending. The old hard thresholds (HI=0.65 = linear 3.18) put
 * the highlight band ABOVE the brightest possible sRGB pixel, so the
 * slider had no visible effect on any sRGB-bound image. New edges keep
 * Lightroom-style perception: Highlights affects upper midtones+, Whites
 * is the top end, Shadows tapers from black to mid-grey, Blacks is the
 * deepest band only. Bands overlap deliberately so adjacent sliders
 * compose smoothly. */
const HI_LO = 0.42;  /* highlights start fading in just above mid-grey   */
const HI_HI = 0.55;  /* highlights fully active by SDR white             */
const SH_LO = 0.07;  /* shadows fully active at sRGB black               */
const SH_HI = 0.40;  /* shadows fade out before mid-grey                 */
const WH_LO = 0.50;  /* whites engage at upper midtones                  */
const WH_HI = 0.60;  /* whites fully active at + slightly above SDR white */
const BL_LO = 0.05;  /* blacks fully active at sRGB ≤ 5                  */
const BL_HI = 0.20;  /* blacks fade out at sRGB ~ 32                    */

/* Grade-lift ACEScct shadow mask (Codex morning-bugs r1 STOP-2): replaces
 * the linear `1 - clamp01(lumN)` mask that bled into mids. By mid-grey
 * (0.4135 ACEScct) lift contribution is < 4%. */
const GLIFT_LO = 0.10;
const GLIFT_HI = 0.45;

/* Mid-grey pivot in ACEScg-linear. Used by Contrast and Dehaze. */
const ACES_MID = 0.18;

/* Curve LUT lookup with linear interpolation. Input must be in [0,1] —
 * curves are evaluated on ACEScct-encoded values which clamp naturally. */
function curveLookupF(lut, v) {
    const N = lut.length - 1;
    if (v <= 0) return lut[0];
    if (v >= 1) return lut[N];
    const idx = v * N;
    const lo = idx | 0;
    const frac = idx - lo;
    return lut[lo] + (lut[lo + 1] - lut[lo]) * frac;
}

/**
 * Render `(w, h)` ACEScg-linear Float32 RGBA pixels through the pipeline.
 *
 * @param {Float32Array} sf       — source RGBA in ACEScg-linear, length=w*h*4
 * @param {Float32Array} dst      — destination ACEScg-linear, same length
 * @param {Uint8ClampedArray} srcU8 — original sRGB Uint8 source (alpha pass-through)
 * @param {number} w
 * @param {number} h              — kept for parity (unused)
 * @param {object} ctx            — buildPipelineContext() output
 * @param {object} opts           — { wantHi, wantLo, clipMask?: Uint8Array }
 */
export function applyKernel(sf, dst, srcU8, w, h, ctx, opts) {
    const {
        lutM, lutR, lutG, lutB,
        gLiftR, gLiftG, gLiftB, gLiftY,
        gGammaR, gGammaG, gGammaB, gGammaY,
        gGainR, gGainG, gGainB, gGainY,
        gOffsetM, gOffRf, gOffGf, gOffBf, gOffMf,
        gCont, gPivot, gColorBoost, gSat, gHue, gLumMix,
        gShadows, gHighlights, gShadowsF, gHighlightsF,
        hasGrade,
        bright, contF, temp, tint, sat, vib,
        hiAdj, shAdj, whAdj, blAdj, dehaze, hasHsl,
        a,
    } = ctx;
    const wantHi   = opts.wantHi;
    const wantLo   = opts.wantLo;
    const clipMask = opts.clipMask || null;
    /* Phase 4.B.select.1 — Selection mask gate. null === everything
     * selected → skip the per-pixel blend entirely so the pixel-hash
     * + worker-parity baselines stay byte-identical to pre-Wave-8.
     * When non-null we apply mask[i>>2] as a linear blend between the
     * adjusted pixel (dst) and the source pixel (sf) so unselected
     * pixels (mask=0) pass through unchanged and feathered edges
     * (mask=0..1) blend smoothly. */
    const selectionMask = (ctx.selection && ctx.selection.mask instanceof Float32Array)
        ? ctx.selection.mask
        : null;

    /* Pre-load matrix coefficients for the HSL-via-sRGB-linear detour. */
    const ma = M_ACEScg_to_sRGB;
    const ma0 = ma[0], ma1 = ma[1], ma2 = ma[2];
    const ma3 = ma[3], ma4 = ma[4], ma5 = ma[5];
    const ma6 = ma[6], ma7 = ma[7], ma8 = ma[8];
    const mb = M_sRGB_to_ACEScg;
    const mb0 = mb[0], mb1 = mb[1], mb2 = mb[2];
    const mb3 = mb[3], mb4 = mb[4], mb5 = mb[5];
    const mb6 = mb[6], mb7 = mb[7], mb8 = mb[8];

    void w;

    for (let i = 0; i < sf.length; i += 4) {
        let r = sf[i], g = sf[i + 1], b = sf[i + 2];
        const r0 = r, g0 = g, b0 = b;            /* preserved for Lum Mix */

        /* Brightness — linear additive in ACEScg. */
        r += bright; g += bright; b += bright;

        /* Contrast — pivot at ACES mid-grey (0.18). */
        r = (r - ACES_MID) * contF + ACES_MID;
        g = (g - ACES_MID) * contF + ACES_MID;
        b = (b - ACES_MID) * contF + ACES_MID;

        /* Tone ranges in ACEScct perceptual space. ACEScct compresses the
         * scene-referred range to a near-linear log axis, so thresholds
         * behave like Lightroom's Highlights / Shadows / Whites / Blacks. */
        let lumLin = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        if (hiAdj !== 0 || shAdj !== 0 || whAdj !== 0 || blAdj !== 0) {
            const lumCT = encodeACEScct(lumLin > 1e-6 ? lumLin : 1e-6);
            if (hiAdj !== 0) {
                const f = smoothstep(lumCT, HI_LO, HI_HI);
                r += hiAdj * f; g += hiAdj * f; b += hiAdj * f;
            }
            if (shAdj !== 0) {
                const f = 1 - smoothstep(lumCT, SH_LO, SH_HI);
                r += shAdj * f; g += shAdj * f; b += shAdj * f;
            }
            if (whAdj !== 0) {
                const f = smoothstep(lumCT, WH_LO, WH_HI);
                r += whAdj * f; g += whAdj * f; b += whAdj * f;
            }
            if (blAdj !== 0) {
                const f = 1 - smoothstep(lumCT, BL_LO, BL_HI);
                r += blAdj * f; g += blAdj * f; b += blAdj * f;
            }
        }

        /* Temperature: warm (R+, B-) / cool (R-, B+). Scalar-additive in
         * linear keeps the same feel; magnitude tuned for ACEScg primaries. */
        r += temp * 0.1569; b -= temp * 0.1569;
        g += tint * 0.1176;

        /* Dehaze — pivot at mid-grey 0.18. */
        if (dehaze !== 0) {
            r = r + (r - ACES_MID) * dehaze * 0.3 + dehaze * 0.03529;
            g = g + (g - ACES_MID) * dehaze * 0.3 + dehaze * 0.02353;
            b = b + (b - ACES_MID) * dehaze * 0.3;
        }

        /* Saturation / Vibrance — chroma math is space-invariant. */
        lumLin = 0.2126 * r + 0.7152 * g + 0.0722 * b;
        r = lumLin + (r - lumLin) * sat;
        g = lumLin + (g - lumLin) * sat;
        b = lumLin + (b - lumLin) * sat;

        if (vib !== 0) {
            const mx = r > g ? (r > b ? r : b) : (g > b ? g : b);
            const mn = r < g ? (r < b ? r : b) : (g < b ? g : b);
            const curSat = mx > 0.001 ? (mx - mn) / mx : 0;
            const vibF = 1 + vib * (1 - curSat) * 1.5;
            r = lumLin + (r - lumLin) * vibF;
            g = lumLin + (g - lumLin) * vibF;
            b = lumLin + (b - lumLin) * vibF;
        }

        /* HSL stage — Codex fix #6: detour through display-linear sRGB.
         * HSL geometry is defined for display-RGB; running it natively in
         * AP1 produces nonsense hue rotations. */
        if (hasHsl) {
            const lr = ma0 * r + ma1 * g + ma2 * b;
            const lg = ma3 * r + ma4 * g + ma5 * b;
            const lb = ma6 * r + ma7 * g + ma8 * b;
            const rc = clamp01(lr), gc = clamp01(lg), bc = clamp01(lb);
            const hsl = rgbToHslF(rc, gc, bc);
            let hDeg = hsl[0] * 360, sV = hsl[1], lV = hsl[2];
            for (const [name, [center, range]] of Object.entries(HSL_RANGES)) {
                const hShift = a['hsl' + name + 'H'];
                const sShift = a['hsl' + name + 'S'];
                const lShift = a['hsl' + name + 'L'];
                if (hShift === 0 && sShift === 0 && lShift === 0) continue;
                let diff = hDeg - center;
                if (diff > 180) diff -= 360;
                if (diff < -180) diff += 360;
                const w2 = Math.max(0, 1 - Math.abs(diff) / range);
                if (w2 <= 0) continue;
                const ww = w2 * w2 * (3 - 2 * w2);   /* smoothstep */
                hDeg += hShift * 0.5 * ww;
                sV = clamp(sV + (sShift / 100) * ww, 0, 1);
                lV = clamp(lV + (lShift / 200) * ww, 0, 1);
            }
            if (hDeg < 0) hDeg += 360;
            if (hDeg >= 360) hDeg -= 360;
            const rgb2 = hslToRgbF(hDeg / 360, sV, lV);
            /* linear-sRGB → ACEScg */
            r = mb0 * rgb2[0] + mb1 * rgb2[1] + mb2 * rgb2[2];
            g = mb3 * rgb2[0] + mb4 * rgb2[1] + mb5 * rgb2[2];
            b = mb6 * rgb2[0] + mb7 * rgb2[1] + mb8 * rgb2[2];
        }

        /* Curves — Codex fix #5: evaluate in ACEScct so a 1° slope at
         * mid-grey lifts mids the way Resolve does, not the way naïve
         * linear-light math does. */
        const cr = encodeACEScct(r > 1e-6 ? r : 1e-6);
        const cg = encodeACEScct(g > 1e-6 ? g : 1e-6);
        const cb = encodeACEScct(b > 1e-6 ? b : 1e-6);
        r = decodeACEScct(curveLookupF(lutM, curveLookupF(lutR, cr)));
        g = decodeACEScct(curveLookupF(lutM, curveLookupF(lutG, cg)));
        b = decodeACEScct(curveLookupF(lutM, curveLookupF(lutB, cb)));

        /* CDL Color Grading — operates natively on linear scene-referred. */
        if (hasGrade) {
            let lumN = 0.2126 * r + 0.7152 * g + 0.0722 * b;

            /* Grade Lift mask — Phase 1.H morning-bugs fix: ACEScct shadow
             * mask via smoothstep instead of linear clamp01 in scene-light.
             * The old `1 - clamp01(lumN)` mask was 0.82 at mid-grey (lift
             * bled into midtones); the new mask is < 4% at mid-grey. */
            const lumCTLift = encodeACEScct(lumN > 1e-6 ? lumN : 1e-6);
            const shadowW = 1 - smoothstep(lumCTLift, GLIFT_LO, GLIFT_HI);
            r += gLiftR * shadowW; g += gLiftG * shadowW; b += gLiftB * shadowW;
            if (gLiftY !== 0) { const lAdj = gLiftY * shadowW; r += lAdj; g += lAdj; b += lAdj; }

            if (gGammaR !== 0 || gGammaG !== 0 || gGammaB !== 0 || gGammaY !== 0) {
                const grV = 1 / Math.max(0.01, 1 + gGammaR + gGammaY);
                const ggV = 1 / Math.max(0.01, 1 + gGammaG + gGammaY);
                const gbV = 1 / Math.max(0.01, 1 + gGammaB + gGammaY);
                if (r > 0) r = Math.pow(r, grV);
                if (g > 0) g = Math.pow(g, ggV);
                if (b > 0) b = Math.pow(b, gbV);
            }

            r *= (1 + gGainR + gGainY);
            g *= (1 + gGainG + gGainY);
            b *= (1 + gGainB + gGainY);

            r += gOffRf; g += gOffGf; b += gOffBf;
            if (gOffsetM !== 0) { r += gOffMf; g += gOffMf; b += gOffMf; }

            if (gCont !== 1.0) {
                r = gPivot + (r - gPivot) * gCont;
                g = gPivot + (g - gPivot) * gCont;
                b = gPivot + (b - gPivot) * gCont;
            }

            if (gShadows !== 0 || gHighlights !== 0) {
                lumN = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                const lumNCT = encodeACEScct(lumN > 1e-6 ? lumN : 1e-6);
                if (gHighlights !== 0) {
                    const f = smoothstep(lumNCT, HI_LO, HI_HI) * gHighlightsF;
                    r += f; g += f; b += f;
                }
                if (gShadows !== 0) {
                    const f = (1 - smoothstep(lumNCT, SH_LO, SH_HI)) * gShadowsF;
                    r += f; g += f; b += f;
                }
            }

            if (gColorBoost !== 0) {
                lumN = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                const mx2 = r > g ? (r > b ? r : b) : (g > b ? g : b);
                const mn2 = r < g ? (r < b ? r : b) : (g < b ? g : b);
                const cSat = mx2 > 0.001 ? (mx2 - mn2) / mx2 : 0;
                const boostF = 1 + (gColorBoost / 50) * (1 - cSat);
                r = lumN + (r - lumN) * boostF;
                g = lumN + (g - lumN) * boostF;
                b = lumN + (b - lumN) * boostF;
            }

            if (gSat !== 50) {
                lumN = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                const satF = gSat / 50;
                r = lumN + (r - lumN) * satF;
                g = lumN + (g - lumN) * satF;
                b = lumN + (b - lumN) * satF;
            }

            if (gHue !== 50) {
                /* Hue rotation: detour through display-linear sRGB exactly
                 * like the HSL stage so hue spin is geometrically valid. */
                const hueShift = (gHue - 50) * 3.6;
                const lr = ma0 * r + ma1 * g + ma2 * b;
                const lg = ma3 * r + ma4 * g + ma5 * b;
                const lb = ma6 * r + ma7 * g + ma8 * b;
                const hslG = rgbToHslF(clamp01(lr), clamp01(lg), clamp01(lb));
                let hd = hslG[0] * 360 + hueShift;
                if (hd < 0) hd += 360;
                if (hd >= 360) hd -= 360;
                const rgbH = hslToRgbF(hd / 360, hslG[1], hslG[2]);
                r = mb0 * rgbH[0] + mb1 * rgbH[1] + mb2 * rgbH[2];
                g = mb3 * rgbH[0] + mb4 * rgbH[1] + mb5 * rgbH[2];
                b = mb6 * rgbH[0] + mb7 * rgbH[1] + mb8 * rgbH[2];
            }

            /* Lum Mix — both `r0/g0/b0` (saved at loop top) and `r/g/b` are
             * ACEScg-linear; same-space luminance is consistent. */
            if (gLumMix < 100) {
                const origLum   = 0.2126 * r0 + 0.7152 * g0 + 0.0722 * b0;
                const gradedLum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
                const mixF = gLumMix / 100;
                const targetLum = origLum * (1 - mixF) + gradedLum * mixF;
                if (gradedLum > 0.001) {
                    const scale = targetLum / gradedLum;
                    r *= scale; g *= scale; b *= scale;
                }
            }
        }

        /* Phase 4.B.select.1 — Selection-mask blend. When mask=null the
         * branch is skipped entirely (zero per-pixel cost → baselines
         * stay byte-identical). When mask=1.0 the blend is a no-op
         * (fully selected). Soft-edge / partial mask coverage produces
         * a linear blend between source and adjusted result.
         *
         * Codex 4.B.select.1 STOP-1 fix: the blend MUST run BEFORE clip
         * detection. Otherwise unselected pixels (mask=0) — which are
         * about to be reverted to their source values — would still flag
         * clipping based on the adjusted r/g/b path we're about to throw
         * away. Same issue for feathered pixels: the clipping overlay
         * would report colors that aren't actually rendered. */
        if (selectionMask) {
            const s = selectionMask[i >> 2];
            if (s < 1) {
                const inv = 1 - s;
                r = r * s + sf[i]     * inv;
                g = g * s + sf[i + 1] * inv;
                b = b * s + sf[i + 2] * inv;
            }
        }

        /* Clip detection in ACEScg space. ">1" means "above scene-referred
         * 1.0", which the view transform tone-maps; ≤0 is the same as before.
         * Runs AFTER the selection blend so unselected / feathered pixels
         * report clipping based on what's actually rendered. */
        if (clipMask) {
            let flag = 0;
            if (wantHi && (r >= 1 || g >= 1 || b >= 1)) flag |= 1;
            if (wantLo && r <= 0 && g <= 0 && b <= 0)    flag |= 2;
            if (flag) clipMask[i >> 2] = flag;
        }

        /* Float32 ACEScg output — NO clamp, NO encode, NO uint8 quantize.
         * Highlights >1 survive for the view transform's filmicACES stage. */
        dst[i]     = r;
        dst[i + 1] = g;
        dst[i + 2] = b;
        dst[i + 3] = srcU8 ? srcU8[i + 3] / 255 : 1;
    }
}
